import type { FaqEntry } from "@/lib/faq-schema";

/**
 * Short, search-oriented questions manufacturers ask before choosing software.
 * Rendered on industry pages and mirrored into FAQPage structured data.
 */
const GENERIC: FaqEntry[] = [
  {
    q: "Can factory.online fit our production model?",
    a: "Yes. Make-to-order, make-to-stock and mixed production run on the same system: routings, operations and material needs follow the way your plant actually works.",
  },
  {
    q: "How long does implementation take?",
    a: "Most manufacturers start with items, BOMs and stock in the first weeks, then add planning, purchasing and costing step by step. There is no plant shutdown involved.",
  },
  {
    q: "Do we keep traceability of materials and batches?",
    a: "Every batch, serial number and consumption is recorded, so you can trace a finished order back to the raw material and supplier delivery.",
  },
];

export const INDUSTRY_FAQ: Record<string, FaqEntry[]> = {
  metalworking: [
    {
      q: "Does it handle sheet, bar and profile stock in different units?",
      a: "Yes. Stock is kept in your own units — pieces, metres, kilograms — with conversion on issue, so cutting plans and inventory stay consistent.",
    },
    {
      q: "Can we plan machine load across CNC, welding and finishing?",
      a: "Operations are scheduled per work centre, so you see machine and operator load, bottlenecks and realistic delivery dates for each order.",
    },
    ...GENERIC.slice(1, 3),
  ],
  "metals-mechanical-engineering": [
    {
      q: "Can we manage multi-level assemblies and fast engineering changes?",
      a: "Multi-level BOMs, revisions, firmware versions and change notes are tracked, so the shop always builds the approved version and never an outdated one.",
    },
    {
      q: "How do we keep control across several distributed production sites?",
      a: "Stock, transit and work status from every site are consolidated in one view, with role-based access so sensitive site data stays restricted.",
    },
    {
      q: "Is serial-number traceability supported end to end?",
      a: "Each unit and component can be tracked by serial or lot from supplier receipt through assembly, test and handover, including subcontract stages.",
    },
    {
      q: "Can we recalculate cost when components or exchange rates change?",
      a: "Planned and actual cost per order are kept side by side, so a change in component prices or currency is reflected in the unit cost right away.",
    },
    ...GENERIC.slice(1, 3),
  ],
  "cable-wire-manufacturing": [
    {
      q: "Does it support drums, lengths and scrap on cable production?",
      a: "Production is tracked by drum and length, including off-cuts and scrap, so stock reflects usable lengths rather than abstract quantities.",
    },
    {
      q: "Can copper price changes be reflected in cost and quotes?",
      a: "Material cost can follow metal price updates, so product cost and quotations stay aligned with the current copper or aluminium price.",
    },
    ...GENERIC.slice(1, 3),
  ],
  "fiber-optic-cables": [
    {
      q: "Can we control fibre batches and colour sequences?",
      a: "Fibre batches, module colouring and colour sequences are kept per order, so production follows the specification and waste of fixed fibre batches is minimised.",
    },
    {
      q: "Are per-drum optical test records kept with the order?",
      a: "Test values are recorded per drum and stay linked to the production order, so certificates and customer documentation are ready at shipment.",
    },
    ...GENERIC.slice(1, 3),
  ],
  "aluminum-extrusion": [
    {
      q: "Can we plan press campaigns and die changes?",
      a: "Orders can be grouped into press campaigns by alloy and die, so setup time drops and the schedule shows realistic press capacity.",
    },
    {
      q: "How is billet consumption and scrap accounted for?",
      a: "Billet issue, recovery rate and scrap are recorded per run, so yield and real cost per kilogram are visible instead of estimated.",
    },
    ...GENERIC.slice(1, 3),
  ],
  "food-and-beverage": [
    {
      q: "Do you support shelf life, lots and recall traceability?",
      a: "Lots carry expiry dates and full genealogy, so a recall query returns the affected batches and customers in minutes.",
    },
    {
      q: "Can recipes with variable yield be handled?",
      a: "Recipes support yield, losses and by-products, so planned and actual consumption match what happens in the process.",
    },
    ...GENERIC.slice(1, 3),
  ],
  "food-feed-production": [
    {
      q: "Can we trace every ingredient lot through to the finished feed?",
      a: "Ingredient lots, mixing batches and finished pallets are linked, so traceability and audit requests are answered from one record.",
    },
    {
      q: "Are recipe changes and quality checks tracked?",
      a: "Recipe versions and quality results are stored with each batch, so you know exactly what was produced under which specification.",
    },
    ...GENERIC.slice(1, 3),
  ],
  "chemical-process-manufacturing": [
    {
      q: "Does it handle batch processes, yields and by-products?",
      a: "Formulations support yield, losses and by-products, and actual consumption is captured per batch for real cost and compliance records.",
    },
    {
      q: "Can we keep certificates of analysis with each batch?",
      a: "Quality results and documents stay attached to the batch, so certificates ship together with the goods.",
    },
    ...GENERIC.slice(1, 3),
  ],
  "jobshop-custom-production": [
    {
      q: "How fast can we quote one-off jobs?",
      a: "Quotes are built from materials, operations and outsourcing, so a custom job gets a realistic price and lead time within minutes.",
    },
    {
      q: "Can we see profitability per job?",
      a: "Planned versus actual material, labour and subcontracting is compared per order, so you see which jobs really make money.",
    },
    ...GENERIC.slice(1, 3),
  ],
};

export function industryFaq(slug: string): FaqEntry[] {
  return INDUSTRY_FAQ[slug] ?? GENERIC;
}

/** FAQ for the industries hub page. */
export const INDUSTRIES_FAQ: FaqEntry[] = [
  {
    q: "Which industries does factory.online cover?",
    a: "Metalworking, cable and wire, fibre optics, aluminium extrusion, food and feed, chemical process manufacturing and custom job shops — with planning, stock and costing adapted to each model.",
  },
  {
    q: "What if our industry is not listed?",
    a: "The platform is configurable: items, BOMs, routings and units follow your process. Tell us your production model and we will show the closest setup.",
  },
  ...GENERIC.slice(1, 3),
];

/** FAQ for the blog hub page. */
export const BLOG_FAQ: FaqEntry[] = [
  {
    q: "What do you write about?",
    a: "Practical manufacturing topics: production planning and MRP, scheduling and machine load, inventory accuracy, purchasing and product costing.",
  },
  {
    q: "Who are the articles for?",
    a: "Owners, production managers and planners in small and mid-sized factories who want fewer stockouts, clearer schedules and honest product cost.",
  },
  {
    q: "Are the articles available in our language?",
    a: "Articles are published in the languages of the site, so you can read planning and inventory guidance in your own language.",
  },
];

/** FAQ for the news hub page. */
export const NEWS_FAQ: FaqEntry[] = [
  {
    q: "What kind of news do you publish?",
    a: "Product releases and new modules, integrations with accounting and e-commerce systems, partnerships and manufacturing trade fairs we attend.",
  },
  {
    q: "How often is factory.online updated?",
    a: "New features and improvements ship continuously and are available to every customer without a separate upgrade project or downtime.",
  },
  {
    q: "Where can I see what changed in the system?",
    a: "Release notes are published here in the news section, with a short description of what changed in planning, stock, purchasing or costing.",
  },
  {
    q: "Can we meet your team in person?",
    a: "Yes. We announce manufacturing exhibitions and events in advance, so you can book a meeting and see the system in a live demo.",
  },
];
